import React, { Component } from 'react'
import PageHeaderWrapper from '../../../components/PageHeaderWrapper'
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Input,
  Button,
  Collapse,
  Table,
  Tag,
  Spin,
  Pagination,
  Icon,
  Dropdown,
  Menu,
  Tooltip,
  notification,
} from 'antd'
import moment from 'moment'
import _ from 'lodash'
import { connect } from 'react-redux'
import Request, {
  productionStatusList,
  getProductionStatusColor,
} from '../../../request'
import { getUrlPath } from '../../../routes'
import ReviewModal from './reviewModal'
import styles from './styles.less'

const { Option } = Select
const { RangePicker } = DatePicker
const { Panel } = Collapse

const fmt = (d) => (d ? moment(d).format('ll') : '-')

const plural = (n, word) => `${n} ${word}${Number(n) === 1 ? '' : 's'}`

const SUBCATEGORY_LABELS = {
  general: 'No Category',
  consumable: 'Consumable',
  manufacturing_order_outward: 'Manufacturing Order',
  dispatch_order: 'Dispatch Order',
  repair_order: 'Repair Order',
  outward_for_guard: 'Outward For Guard',
}

// known subcategories first (general last), then any other alphabetically
const SUBCATEGORY_ORDER = [
  'consumable',
  'manufacturing_order_outward',
  'dispatch_order',
  'repair_order',
  'outward_for_guard',
  'general',
]

const subCategoryLabel = (key) => SUBCATEGORY_LABELS[key] || key

const sortSubCategories = (keys) =>
  [...keys].sort((a, b) => {
    const ai = SUBCATEGORY_ORDER.indexOf(a)
    const bi = SUBCATEGORY_ORDER.indexOf(b)
    if (ai === -1 && bi === -1) return String(a).localeCompare(String(b))
    if (ai === -1) return 1
    if (bi === -1) return -1
    return ai - bi
  })

class OutwardOrderList extends Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: false,
      orders: [],
      total: 0,
      page: 1,
      pageSize: 10,
      activeKeys: [],
      // filters
      dateRange: null,
      subCategory: undefined,
      user: undefined,
      orderId: '',
      status: undefined,
      // modal (date-driven)
      modalVisible: false,
      activeGroup: null,
      // inwardId -> dispatch (Outward Form) record, for product names + Edit
      formMap: {},
    }
  }

  componentDidMount() {
    this.fetchList()
  }

  buildParams = () => {
    const { page, pageSize, dateRange, subCategory, user, orderId, status } =
      this.state
    const params = { page: page, count: pageSize, results: pageSize }
    if (dateRange && dateRange.length === 2) {
      params.startDate = dateRange[0].startOf('day').toISOString()
      params.endDate = dateRange[1].endOf('day').toISOString()
    }
    if (subCategory) params.subCategory = subCategory
    if (user) params.user = user
    if (orderId) params.orderId = orderId
    if (status) params.status = status
    return params
  }

  fetchList = () => {
    this.setState({ loading: true })
    Request.outwardOrderList(this.buildParams()).then((res) => {
      const { error, data, total } = res || {}
      if (!error) {
        const orders = data || []
        this.setState({
          loading: false,
          orders: orders,
          total: total || orders.length,
          activeKeys: this.computeGroupKeys(orders),
        })
        this.fetchForms(orders)
      } else {
        this.setState({ loading: false, orders: [], total: 0 })
        notification.error({ message: 'Failed to load outward orders' })
      }
    })
  }

  // For each order's inwardId, fetch the linked Outward Form (dispatch) record so
  // its product names can be shown in the date-group header and edited. Cached in
  // formMap to avoid refetching.
  fetchForms = (orders) => {
    const ids = []
    ;(orders || []).forEach((o) => {
      if (o && o.inwardId && ids.indexOf(String(o.inwardId)) === -1) {
        ids.push(String(o.inwardId))
      }
    })
    ids.forEach((id) => {
      if (this.state.formMap[id]) return
      Request.getDispatchInwardOutwardById({ _id: id }).then(({ data, error }) => {
        if (!error && data) {
          this.setState((s) => ({ formMap: { ...s.formMap, [id]: data } }))
        }
      })
    })
  }

  // Distinct Outward Forms referenced by a date group (one per unique inwardId)
  getGroupForms = (group) => {
    const seen = {}
    const forms = []
    ;(group.orders || []).forEach((o) => {
      const id = o && o.inwardId ? String(o.inwardId) : null
      if (!id || seen[id]) return
      seen[id] = true
      const d = this.state.formMap[id]
      const products =
        d && Array.isArray(d.products)
          ? d.products.map((p) => p && p.productName).filter(Boolean)
          : []
      forms.push({ inwardId: id, products, serialNumber: d && d.serialNumber })
    })
    return forms
  }

  formLabel = (f) =>
    f.products && f.products.length
      ? f.products.join(', ')
      : f.serialNumber || 'Outward Form'

  openOutwardForm = (inwardId) => {
    window.open(getUrlPath('dispatch.editOutwardForm', { id: inwardId }), '_blank')
  }

  // Open the viewer for a single order row only (just that row's media)
  openOrder = (group, order) => {
    if (!order) return this.openGroup(group)
    const key = order._id || order.orderId
    const photos = Number(order.photosCount) || 0
    const videos = Number(order.videosCount) || 0
    this.setState({
      activeGroup: {
        key: group.key,
        label: order.userName ? `${group.label} · ${order.userName}` : group.label,
        mediaCount: photos + videos,
        orderCount: 1,
        orderIds: [key].filter(Boolean),
      },
      modalVisible: true,
    })
  }

  computeGroups = (orders) => {
    const map = {}
    orders.forEach((o) => {
      const key = o.captureDate
        ? moment(o.captureDate).format('YYYY-MM-DD')
        : 'unknown'
      if (!map[key]) map[key] = []
      map[key].push(o)
    })
    const keys = Object.keys(map).sort((a, b) => {
      if (a === 'unknown') return 1
      if (b === 'unknown') return -1
      return moment(b, 'YYYY-MM-DD').valueOf() - moment(a, 'YYYY-MM-DD').valueOf()
    })
    return keys.map((k) => ({
      key: k,
      label: k === 'unknown' ? 'Unknown Date' : moment(k, 'YYYY-MM-DD').format('DD MMM YYYY'),
      orders: map[k],
      photosCount: map[k].reduce((s, o) => s + (Number(o.photosCount) || 0), 0),
      videosCount: map[k].reduce((s, o) => s + (Number(o.videosCount) || 0), 0),
      mediaCount: map[k].reduce(
        (s, o) => s + (Number(o.photosCount) || 0) + (Number(o.videosCount) || 0),
        0
      ),
    }))
  }

  computeGroupKeys = (orders) => this.computeGroups(orders).map((g) => g.key)

  handleSearch = () => {
    this.setState({ page: 1 }, this.fetchList)
  }

  handleReset = () => {
    this.setState(
      {
        dateRange: null,
        subCategory: undefined,
        user: undefined,
        orderId: '',
        status: undefined,
        page: 1,
      },
      this.fetchList
    )
  }

  onPageChange = (page, pageSize) => {
    this.setState({ page: page, pageSize: pageSize }, this.fetchList)
  }

  // Date-driven: open the viewer for a whole capture-date group
  openGroup = (group) => {
    this.setState({
      activeGroup: {
        key: group.key,
        label: group.label,
        mediaCount: group.mediaCount,
        orderCount: group.orders ? group.orders.length : 0,
        orderIds: (group.orders || []).map((o) => o._id || o.orderId).filter(Boolean),
      },
      modalVisible: true,
    })
  }

  closeModal = () => {
    this.setState({ modalVisible: false })
  }

  distinct = (key) =>
    _.uniq(this.state.orders.map((o) => o[key]).filter(Boolean))

  // an order can hold media of several subcategories — offer them all
  distinctSubCategories = () =>
    sortSubCategories(
      _.uniq(
        this.state.orders.reduce(
          (acc, o) =>
            acc.concat(
              o.subCategory ? [o.subCategory] : [],
              Object.keys(o.subCategoryCounts || {})
            ),
          []
        )
      )
    )

  renderFilters = () => {
    const { dateRange, subCategory, user, orderId, status } = this.state
    return (
      <Card className={styles.filterBar} bodyStyle={{ paddingBottom: 0 }}>
        <Row gutter={12}>
          <Col xs={24} sm={12} md={8} lg={6}>
            <RangePicker
              style={{ width: '100%', marginBottom: 12 }}
              value={dateRange}
              onChange={(val) => this.setState({ dateRange: val })}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              allowClear
              showSearch
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="Subcategory"
              value={subCategory}
              onChange={(val) => this.setState({ subCategory: val })}
            >
              {this.distinctSubCategories().map((c) => (
                <Option key={c} value={c}>
                  {subCategoryLabel(c)}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Select
              allowClear
              showSearch
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="User"
              value={user}
              onChange={(val) => this.setState({ user: val })}
            >
              {this.distinct('userName').map((u) => (
                <Option key={u} value={u}>
                  {u}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={4}>
            <Input
              style={{ marginBottom: 12 }}
              placeholder="Order ID"
              value={orderId}
              allowClear
              onChange={(e) => this.setState({ orderId: e.target.value })}
              onPressEnter={this.handleSearch}
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={5}>
            <Select
              allowClear
              style={{ width: '100%', marginBottom: 12 }}
              placeholder="Status"
              value={status}
              onChange={(val) => this.setState({ status: val })}
            >
              {productionStatusList.map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button
              type="primary"
              icon="search"
              onClick={this.handleSearch}
              style={{ marginRight: 8, marginBottom: 12 }}
            >
              Search
            </Button>
            <Button icon="reload" onClick={this.handleReset} style={{ marginBottom: 12 }}>
              Reset
            </Button>
          </Col>
        </Row>
      </Card>
    )
  }

  getColumns = () => [
    {
      title: 'Order ID',
      key: 'orderId',
      width: 130,
      render: (val, r) =>
        r.orderId ? <b>#{r.orderId}</b> : <span style={{ color: '#bbb' }}>-</span>,
    },
    { title: 'User Name', dataIndex: 'userName', key: 'userName' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    {
      title: 'Subcategory',
      key: 'subCategory',
      render: (val, r) => {
        const counts = r.subCategoryCounts || {}
        const keys = sortSubCategories(Object.keys(counts))
        // when the order isn't linked to any Outward form, its images are all
        // "not approved" — show that count (blue) beside the completed/rejected
        const notMapped = !r.inwardId
        // older API responses have no per-subcategory counts — show the
        // single (first) subcategory as before
        if (!keys.length) return subCategoryLabel(r.subCategory) || '-'
        return keys.map((k) => {
          const c = counts[k] || {}
          return (
            <div key={k} className={styles.subCatLine}>
              <span className={styles.subCatLabel}>{subCategoryLabel(k)}:</span>
              <Tag color="green">{Number(c.completed) || 0}</Tag>
              <Tag color="red">{Number(c.rejected) || 0}</Tag>
              {notMapped ? (
                <Tooltip title="Not approved (not mapped to any Outward form)">
                  <Tag color="blue">{Number(c.total) || 0}</Tag>
                </Tooltip>
              ) : null}
            </div>
          )
        })
      },
    },
    {
      title: 'Photos',
      key: 'photosCount',
      width: 90,
      render: (val, r) => (
        <Tag>
          <Icon type="picture" /> {Number(r.photosCount) || 0}
        </Tag>
      ),
    },
    {
      title: 'Videos',
      key: 'videosCount',
      width: 90,
      render: (val, r) => (
        <Tag>
          <Icon type="video-camera" /> {Number(r.videosCount) || 0}
        </Tag>
      ),
    },
    {
      title: 'Upload Date',
      key: 'uploadedDate',
      width: 140,
      render: (val, r) => fmt(r.uploadedDate || r.createdAt),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (val, r) => (
        <div>
          <Tag className={styles.statusBadge} color={getProductionStatusColor(val || 'Pending')}>
            {val || 'Pending'}
          </Tag>
          {/* Mapped when every image of the order is attached to an outward form,
              else Not Mapped — visible to the employee, not just the admin review */}
          <div style={{ marginTop: 4 }}>
            {r.allMapped ? (
              <Tag color="green">Mapped</Tag>
            ) : (
              <Tag color="blue">Not Mapped</Tag>
            )}
          </div>
        </div>
      ),
    },
  ]

  render() {
    const { loading, orders, total, page, pageSize, activeKeys, modalVisible, activeGroup } =
      this.state
    const groups = this.computeGroups(orders)
    const columns = this.getColumns()

    return (
      <PageHeaderWrapper title={`Outward Order List`}>
        {this.renderFilters()}

        <Card bordered>
          <Spin spinning={loading}>
            {groups.length === 0 ? (
              <div className={styles.emptyWrap}>
                <Icon type="inbox" style={{ fontSize: 36, display: 'block', marginBottom: 8 }} />
                No outward orders found
              </div>
            ) : (
              <Collapse
                bordered={false}
                className={styles.collapseWrap}
                activeKey={activeKeys}
                onChange={(keys) => this.setState({ activeKeys: keys })}
              >
                {groups.map((g) => {
                  // a date group's media may be attached to one or more Outward
                  // (dispatch) forms — each is independently editable
                  const groupForms = this.getGroupForms(g)
                  return (
                  <Panel
                    key={g.key}
                    extra={
                      <span onClick={(e) => e.stopPropagation()}>
                        {groupForms.length > 1 ? (
                          <Dropdown
                            trigger={['click']}
                            overlay={
                              <Menu>
                                {groupForms.map((f) => (
                                  <Menu.Item
                                    key={f.inwardId}
                                    onClick={() => this.openOutwardForm(f.inwardId)}
                                  >
                                    <Icon type="edit" /> {this.formLabel(f)}
                                  </Menu.Item>
                                ))}
                              </Menu>
                            }
                          >
                            <Button size="small" icon="edit" style={{ marginRight: 8 }}>
                              Edit <Icon type="down" />
                            </Button>
                          </Dropdown>
                        ) : groupForms.length === 1 ? (
                          <Button
                            size="small"
                            icon="edit"
                            style={{ marginRight: 8 }}
                            onClick={() => this.openOutwardForm(groupForms[0].inwardId)}
                          >
                            Edit
                          </Button>
                        ) : null}
                        <Button
                          type="primary"
                          size="small"
                          icon="eye"
                          onClick={() => this.openGroup(g)}
                        >
                          Review
                        </Button>
                      </span>
                    }
                    header={
                      <span className={styles.groupHeader}>
                        <span className={styles.groupDate}>{g.label}</span>
                        <span className={styles.countChip}>
                          <Icon type="picture" /> {plural(g.photosCount, 'Photo')}
                        </span>
                        <span className={styles.countDivider}>|</span>
                        <span className={styles.countChip}>
                          <Icon type="video-camera" /> {plural(g.videosCount, 'Video')}
                        </span>
                        <span className={styles.orderCount}>{plural(g.orders.length, 'Order')}</span>
                        {groupForms.filter((f) => f.products.length).length ? (
                          <span style={{ marginLeft: 8 }} onClick={(e) => e.stopPropagation()}>
                            {groupForms
                              .filter((f) => f.products.length)
                              .map((f) => (
                                <Tooltip key={f.inwardId} title="Edit Outward Form">
                                  <Tag
                                    color="blue"
                                    style={{ cursor: 'pointer' }}
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      this.openOutwardForm(f.inwardId)
                                    }}
                                  >
                                    <Icon type="tag" /> {f.products.join(', ')} <Icon type="edit" />
                                  </Tag>
                                </Tooltip>
                              ))}
                          </span>
                        ) : null}
                      </span>
                    }
                  >
                    <Table
                      className={styles.clickableTable}
                      size="small"
                      rowKey={(r) => r._id || r.orderId}
                      columns={columns}
                      dataSource={g.orders}
                      pagination={false}
                      onRow={(record) => ({
                        onClick: () => this.openOrder(g, record),
                      })}
                    />
                  </Panel>
                  )
                })}
              </Collapse>
            )}

            <div className={styles.paginationBar}>
              <Pagination
                current={page}
                pageSize={pageSize}
                total={total}
                showSizeChanger
                pageSizeOptions={['10', '25', '50', '100']}
                showTotal={(t) => `Total ${t} orders`}
                onChange={this.onPageChange}
                onShowSizeChange={this.onPageChange}
              />
            </div>
          </Spin>
        </Card>

        <ReviewModal
          visible={modalVisible}
          group={activeGroup}
          onClose={this.closeModal}
          onUpdated={this.fetchList}
        />
      </PageHeaderWrapper>
    )
  }
}

const mapStateToProps = ({ global }) => ({
  loading: global.buttonLoading,
})
const mapDispatchToProps = (dispatch) => ({ dispatch })

export default connect(mapStateToProps, mapDispatchToProps)(OutwardOrderList)
